import { getClient, showReportDialog } from '@sentry/browser'
import { DeadFish, ExpandLess, ExpandMore } from '@tunasong/icons'
import { logger } from '@tunasong/models'
import { Button, Typography } from '@tunasong/ui-lib'
import { useId, useState } from 'react'
import type { ReactElement } from 'react'
import styles from './error-general.module.css'

/**
 * Sentry's `ErrorBoundary` hands its fallback `error`, `eventId` and `resetError`; the app's own boundary hands
 * `error` and, when it reported one, `eventId`.
 */
export interface ErrorProps {
  error?: unknown
  /** The Sentry event of this crash: shown as the Error ID, and what the feedback dialog is about */
  eventId?: string
}

const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error ?? 'Unknown error'))

/** What Copy details copies: enough to find the event */
const detailsOf = (error: unknown, eventId?: string) =>
  [
    `Error: ${messageOf(error)}`,
    `Error ID: ${eventId ?? 'none'}`,
    `URL: ${window.location.href}`,
    `Release: ${getClient()?.getOptions().release ?? 'unknown'}`,
  ].join('\n')

/**
 * The full-page crash (GH #2809, the user's approved design, 2026-10-25): what happened, Reload and Go to Home, the
 * technical details on request, and a way to tell us.
 */
/** Typed to return a `ReactElement`, not `FC`'s `ReactNode`: Sentry's `ErrorBoundary` takes it as its `fallback` */
export const ErrorGeneral = (props: ErrorProps): ReactElement => {
  const { error, eventId } = props
  const [showDetails, setShowDetails] = useState(false)
  const detailsId = useId()

  const handleReload = () => window.location.reload()
  const handleCopy = () => {
    navigator.clipboard.writeText(detailsOf(error, eventId)).catch((e: unknown) => logger.warn('Copy failed', e))
  }
  const handleFeedback = () => {
    if (eventId) {
      showReportDialog({ eventId })
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <figure className={styles.figure}>
          <DeadFish className={styles.icon} aria-hidden="true" />
          <figcaption className={styles.caption}>A dead Tuna is a sad Tuna.</figcaption>
        </figure>

        <Typography variant="h4" component="h1" className={styles.heading}>
          This page stopped working
        </Typography>
        <Typography variant="body1" className={styles.body}>
          Something went wrong on our side. We&apos;ve been notified. Reloading usually fixes it.
        </Typography>

        <div className={styles.actions}>
          <Button variant="contained" className={styles.action} onClick={handleReload}>
            Reload
          </Button>
          <Button variant="outlined" className={styles.action} href="/app">
            Go to Home
          </Button>
        </div>

        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={showDetails}
          aria-controls={detailsId}
          onClick={() => setShowDetails(open => !open)}
        >
          Technical details
          {showDetails ? <ExpandLess aria-hidden="true" /> : <ExpandMore aria-hidden="true" />}
        </button>
        {showDetails ? (
          <div id={detailsId} className={styles.details}>
            <code className={styles.message}>{messageOf(error)}</code>
            {eventId ? <div className={styles.eventId}>Error ID {eventId}</div> : null}
            <Button size="small" variant="outlined" onClick={handleCopy}>
              Copy details
            </Button>
          </div>
        ) : null}

        <Typography variant="body2" className={styles.footer}>
          Keeps happening?{' '}
          {eventId ? (
            <>
              <button type="button" className={styles.link} onClick={handleFeedback}>
                Tell us what happened
              </button>{' '}
              or write to <a href="mailto:support@tunasong.com">support@tunasong.com</a>.
            </>
          ) : (
            <>
              Write to <a href="mailto:support@tunasong.com">support@tunasong.com</a>.
            </>
          )}
        </Typography>
      </div>
    </div>
  )
}
